import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { vendorAPI } from '../api/vendorAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function VendorCreateScreen({ isModal = false, onVendorCreated = null, onCancel = null }) {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [vendor, setVendor] = useState({
        name: '',
        full_name: '',
        contact: '',
        bill_to_address: '',
        ship_to_address: '',
        phone: '',
        email: '',
        is_active: true,
        shipping_lead_time_days: ''
    })

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            const newVendor = await vendorAPI.create(vendor)
            if (isModal && onVendorCreated) {
                onVendorCreated(newVendor)
            } else {
                navigate('/vendors')
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleCancel = () => {
        if (isModal && onCancel) {
            onCancel()
        } else {
            navigate('/vendors')
        }
    }

    if (!isModal && permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!isModal && !hasPermission('vendors.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to create vendors.
                </Alert>
            </Container>
        )
    }

    return (
        <Container className={isModal ? 'p-0' : ''}>
            <Row className="justify-content-left">
                <Col md={isModal ? 12 : 8} lg={isModal ? 12 : 6}>
                    {!isModal && <h1>Create Vendor</h1>}
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="name">
                            <Form.Label>Name*</Form.Label>
                            <Form.Control
                                type="text"
                                value={vendor.name}
                                onChange={(e) => setVendor({...vendor, name: e.target.value})}
                                required
                                placeholder="Enter name"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="full_name">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={vendor.full_name}
                                onChange={(e) => setVendor({...vendor, full_name: e.target.value})}
                                placeholder="Enter full name"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="contact">
                            <Form.Label>Contact</Form.Label>
                            <Form.Control
                                type="text"
                                value={vendor.contact}
                                onChange={(e) => setVendor({...vendor, contact: e.target.value})}
                                placeholder="Enter contact"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="bill_to_address">
                            <Form.Label>Bill To Address</Form.Label>
                            <Form.Control
                                type="text"
                                value={vendor.bill_to_address}
                                onChange={(e) => setVendor({...vendor, bill_to_address: e.target.value})}
                                placeholder="Enter billing address"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="ship_to_address">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Ship To Address</Form.Label>
                                {vendor.bill_to_address && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setVendor({...vendor, ship_to_address: vendor.bill_to_address})}
                                        style={{ fontSize: '0.875rem', padding: '0' }}
                                    >
                                        <i className="bi bi-clipboard me-1"></i>
                                        Copy from Bill To
                                    </Button>
                                )}
                            </div>
                            <Form.Control
                                type="text"
                                value={vendor.ship_to_address}
                                onChange={(e) => setVendor({...vendor, ship_to_address: e.target.value})}
                                placeholder="Enter shipping address"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="phone">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={vendor.phone}
                                onChange={(e) => setVendor({...vendor, phone: e.target.value})}
                                placeholder="Enter phone"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={vendor.email}
                                onChange={(e) => setVendor({...vendor, email: e.target.value})}
                                placeholder="Enter email"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="shipping_lead_time_days">
                            <Form.Label>Shipping Lead Time (days)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={vendor.shipping_lead_time_days}
                                onChange={(e) => setVendor({...vendor, shipping_lead_time_days: e.target.value ? parseInt(e.target.value) : ''})}
                                placeholder="Enter estimated shipping days"
                                autoComplete="off"
                            />
                            <Form.Text className="text-muted">
                                Approximate number of days from order placement to delivery
                            </Form.Text>
                        </Form.Group>
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Button variant="success" type="submit" className="me-2">
                            Create
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            onClick={handleCancel}
                            type="button"
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default VendorCreateScreen 